import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { Upload, CheckCircle, XCircle, ChevronDown, ChevronUp, Sparkles, BookOpen } from 'lucide-react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { BOOK_TAXONOMY } from './lib/bookTaxonomy';
import { callClaudeWithImage } from './anthropic';
import {
  parseGoodreadsRow, parseStoryGraphRow, findExistingBook, detectCsvFormat,
} from './lib/csvImportUtils';
import { setDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

/* ═══════════════════════════════════════════
   BOOK IMPORT TOOLS COMPONENT
   Goodreads CSV, StoryGraph CSV, Batch Screenshot
═══════════════════════════════════════════ */
export default function BookImportTools({ existingBooks, onImportComplete, onClose }) {
  const [activeImport, setActiveImport] = useState(null); // 'goodreads' | 'storygraph' | 'screenshot'

  // CSV state
  const [csvData, setCsvData] = useState(null);
  const [csvFormat, setCsvFormat] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null); // { matched, newBooks, skipped, total }
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvFileRef = useRef(null);

  // Screenshot batch state
  const [screenshots, setScreenshots] = useState([]);
  const [ssProcessing, setSsProcessing] = useState(false);
  const [ssProgress, setSsProgress] = useState({ current: 0, total: 0 });
  const [ssResults, setSsResults] = useState([]); // array of { imageSrc, title, author, tags, confidence, needsTitle }
  const [ssActiveTab, setSsActiveTab] = useState(0);
  const [ssImporting, setSsImporting] = useState(false);
  const [ssImportResult, setSsImportResult] = useState(null);
  const ssFileRef = useRef(null);

  /* ─────────────────────────────────────────
     CSV IMPORT (Goodreads & StoryGraph)
  ───────────────────────────────────────── */
  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (results) => {
        const cols = Object.keys(results.data[0] || {});
        const format = detectCsvFormat(cols);
        setCsvFormat(format);
        setCsvData(results.data);
        setCsvResult(null);

        if (format === 'unknown') return;

        // Build preview
        const parser = format === 'goodreads' ? parseGoodreadsRow : parseStoryGraphRow;
        let matched = 0, newBooks = 0, skipped = 0;
        const parsed = [];

        for (const row of results.data) {
          const book = parser(row);
          if (!book.title) { skipped++; continue; }
          const existing = findExistingBook(book, existingBooks);
          parsed.push({ ...book, existingMatch: existing });
          if (existing) matched++;
          else newBooks++;
        }

        setCsvPreview({ matched, newBooks, skipped, total: results.data.length, parsed });
      },
      error: () => alert('Failed to parse CSV file'),
    });
    if (e.target) e.target.value = '';
  };

  const handleCsvImport = async () => {
    if (!csvPreview?.parsed) return;
    setCsvImporting(true);

    let added = 0, matchedExisting = 0, errors = 0;

    for (const book of csvPreview.parsed) {
      try {
        if (book.existingMatch) {
          // Book exists — update with import metadata
          const bookId = book.existingMatch.id || book.existingMatch.googleBooksId;
          const updateData = {};
          if (book.rating) updateData[`${book.source}Rating`] = book.rating;
          if (book.source) updateData.lastImportSource = book.source;
          if (book.pace) updateData.pace = book.pace;

          // Merge auto tags into existing autoTags
          if (Object.keys(book.autoTags).length > 0) {
            const existing = book.existingMatch.autoTags || {};
            const merged = { ...existing };
            for (const [secKey, items] of Object.entries(book.autoTags)) {
              merged[secKey] = { ...(merged[secKey] || {}), ...items };
            }
            updateData.autoTags = merged;
          }

          if (Object.keys(updateData).length > 0) {
            await updateDoc(doc(db, 'bookDatabase', bookId), updateData);
          }
          matchedExisting++;
        } else {
          // New book — look up on Google Books by ISBN or title
          const q = book.isbn
            ? `isbn:${book.isbn}`
            : `intitle:${book.title}${book.author ? `+inauthor:${book.author}` : ''}`;
          const resp = await fetch(
            `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(q)}&maxResults=1`
          );
          const data = await resp.json();

          if (!data.items?.length) { errors++; continue; }

          const item = data.items[0];
          const vol = item.volumeInfo;
          const gbId = item.id;

          // Check it doesn't already exist by Google Books ID
          const existingDoc = await getDoc(doc(db, 'bookDatabase', gbId));
          if (existingDoc.exists()) { matchedExisting++; continue; }

          await setDoc(doc(db, 'bookDatabase', gbId), {
            title: vol.title || book.title,
            author: (vol.authors || []).join(', ') || book.author,
            googleBooksId: gbId,
            isbn: book.isbn || (vol.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || null,
            cover: vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
            description: (vol.description || '').substring(0, 300),
            pageCount: vol.pageCount || book.pages || null,
            publishedDate: vol.publishedDate || null,
            googleBooksLink: vol.infoLink || null,
            autoTags: book.autoTags,
            adminTags: {},
            resolvedTags: {},
            communityTags: {},
            submissionCount: 0,
            needsTagging: true,
            [`${book.source}Rating`]: book.rating || null,
            lastImportSource: book.source,
            pace: book.pace || null,
            dateAdded: new Date().toISOString(),
            addedBy: 'admin-csv-import',
          });
          added++;
        }
        // Rate limit
        await new Promise(r => setTimeout(r, 100));
      } catch (e) {
        console.error(`[CSV Import] Error for "${book.title}":`, e.message);
        errors++;
      }
    }

    setCsvResult({ added, matchedExisting, errors });
    setCsvImporting(false);
    if (onImportComplete) onImportComplete();
  };

  /* ─────────────────────────────────────────
     BATCH SCREENSHOT IMPORT
  ───────────────────────────────────────── */
  const handleScreenshotUpload = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 20);
    if (files.length === 0) return;
    setScreenshots(files);
    setSsResults([]);
    setSsImportResult(null);
    if (e.target) e.target.value = '';
  };

  const processScreenshots = async () => {
    setSsProcessing(true);
    setSsProgress({ current: 0, total: screenshots.length });
    const results = [];

    const taxonomyRef = BOOK_TAXONOMY.map(sec => {
      const allItems = sec.groups.flatMap(g => g.items);
      return `${sec.key} (${sec.title}): ${allItems.join(', ')}`;
    }).join('\n');

    const systemPrompt = 'You are a book tag extraction assistant. You read screenshots of book database pages and extract all visible tags, tropes, and metadata. Return ONLY valid JSON — no markdown fences, no explanation.';

    for (let i = 0; i < screenshots.length; i++) {
      setSsProgress({ current: i + 1, total: screenshots.length });
      const file = screenshots[i];

      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const mediaType = file.type || 'image/png';
        const imageSrc = URL.createObjectURL(file);

        const prompt = `This is a screenshot from a book database website showing tags for a specific book. Extract: the book title and author if visible, and all tags organised by category. Map to these categories using fuzzy matching: steam_level, genre, relationship, tropes, kinks_and_play, story_elements, content_warnings, heroine_traits, hero_traits, location, time_period, story_structure, pov, length. Return as JSON. For each tag include a confidence score: high (exact/near match), medium (reasonable interpretation), low (best guess). If the book title/author is visible include those too.

Here is our full taxonomy — map to these exact values:
${taxonomyRef}

Return JSON in this format:
{
  "title": "Book Title or null",
  "author": "Author Name or null",
  "tags": {
    "steamLevel": [{"value": "Explicit open door", "confidence": "high"}],
    "genres": [{"value": "High fantasy", "confidence": "high"}, {"value": "Fae", "confidence": "medium"}],
    ...
  }
}`;

        const result = await callClaudeWithImage({
          imageBase64: base64, mediaType, prompt, systemPrompt, maxTokens: 2048,
        });

        const cleaned = result.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
        const detected = JSON.parse(cleaned);

        // Convert to tag form with confidence
        const tags = {};
        const confidence = {};
        for (const section of BOOK_TAXONOMY) {
          const sectionTags = detected.tags?.[section.key];
          if (sectionTags && Array.isArray(sectionTags) && sectionTags.length > 0) {
            const validItems = section.groups.flatMap(g => g.items);
            tags[section.key] = {};
            confidence[section.key] = {};
            for (const entry of sectionTags) {
              const val = typeof entry === 'string' ? entry : entry.value;
              const conf = typeof entry === 'string' ? 'medium' : (entry.confidence || 'medium');
              const exact = validItems.find(item => item.toLowerCase() === (val || '').toLowerCase());
              if (exact) {
                tags[section.key][exact] = 'present';
                confidence[section.key][exact] = conf;
              }
            }
            if (Object.keys(tags[section.key]).length === 0) {
              delete tags[section.key];
              delete confidence[section.key];
            }
          }
        }

        results.push({
          imageSrc,
          title: detected.title || null,
          author: detected.author || null,
          tags,
          confidence,
          needsTitle: !detected.title,
        });
      } catch (err) {
        console.error(`[Screenshot ${i + 1}] Error:`, err.message);
        results.push({
          imageSrc: URL.createObjectURL(file),
          title: null, author: null,
          tags: {}, confidence: {},
          needsTitle: true,
          error: err.message,
        });
      }
    }

    setSsResults(results);
    setSsProcessing(false);
    setSsActiveTab(0);
  };

  const updateSsResult = (idx, field, value) => {
    setSsResults(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeSsTag = (idx, secKey, item) => {
    setSsResults(prev => {
      const updated = [...prev];
      const book = { ...updated[idx] };
      const tags = { ...book.tags };
      const sec = { ...tags[secKey] };
      delete sec[item];
      if (Object.keys(sec).length === 0) delete tags[secKey];
      else tags[secKey] = sec;
      book.tags = tags;
      updated[idx] = book;
      return updated;
    });
  };

  const handleSsImport = async () => {
    setSsImporting(true);
    let imported = 0, errors = 0, needsTitle = 0;

    for (const book of ssResults) {
      if (!book.title) { needsTitle++; continue; }
      try {
        // Look up on Google Books
        const q = `intitle:${book.title}${book.author ? `+inauthor:${book.author}` : ''}`;
        const resp = await fetch(
          `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(q)}&maxResults=1`
        );
        const data = await resp.json();
        if (!data.items?.length) { errors++; continue; }

        const item = data.items[0];
        const vol = item.volumeInfo;
        const gbId = item.id;

        const existing = await getDoc(doc(db, 'bookDatabase', gbId));
        if (existing.exists()) {
          // Merge tags into existing
          const existingData = existing.data();
          const mergedAutoTags = { ...(existingData.autoTags || {}) };
          for (const [secKey, items] of Object.entries(book.tags)) {
            mergedAutoTags[secKey] = { ...(mergedAutoTags[secKey] || {}), ...items };
          }
          await updateDoc(doc(db, 'bookDatabase', gbId), {
            autoTags: mergedAutoTags,
            lastImportSource: 'screenshot-import',
          });
        } else {
          await setDoc(doc(db, 'bookDatabase', gbId), {
            title: vol.title || book.title,
            author: (vol.authors || []).join(', ') || book.author,
            googleBooksId: gbId,
            isbn: (vol.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || null,
            cover: vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
            description: (vol.description || '').substring(0, 300),
            pageCount: vol.pageCount || null,
            publishedDate: vol.publishedDate || null,
            googleBooksLink: vol.infoLink || null,
            autoTags: book.tags,
            adminTags: {},
            resolvedTags: {},
            communityTags: {},
            submissionCount: 0,
            needsTagging: true,
            lastImportSource: 'screenshot-import',
            dateAdded: new Date().toISOString(),
            addedBy: 'admin-screenshot-import',
          });
        }
        imported++;
        await new Promise(r => setTimeout(r, 100));
      } catch (e) {
        console.error(`[Screenshot Import] Error for "${book.title}":`, e.message);
        errors++;
      }
    }

    setSsImportResult({ imported, errors, needsTitle });
    setSsImporting(false);
    if (onImportComplete) onImportComplete();
  };

  /* ─────────────────────────────────────────
     RENDER
  ───────────────────────────────────────── */
  const CONF_COLORS = {
    high: 'bg-teal-100 text-teal-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-red-100 text-red-700',
  };

  return (
    <div>
      {/* Import method selector */}
      {!activeImport && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button onClick={() => setActiveImport('goodreads')}
            className="rounded-xl border-2 border-slate-200 bg-white p-4 text-left hover:border-teal-300 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-5 w-5 text-teal-600" />
              <h4 className="font-bold text-slate-900 text-sm">Goodreads CSV</h4>
            </div>
            <p className="text-xs text-slate-500">Import from Goodreads export CSV — maps shelves to our genre taxonomy, includes ratings.</p>
          </button>
          <button onClick={() => setActiveImport('storygraph')}
            className="rounded-xl border-2 border-slate-200 bg-white p-4 text-left hover:border-teal-300 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2 mb-2">
              <Upload className="h-5 w-5 text-teal-600" />
              <h4 className="font-bold text-slate-900 text-sm">StoryGraph CSV</h4>
            </div>
            <p className="text-xs text-slate-500">Import from StoryGraph export — maps moods, genres, and content warnings to our taxonomy.</p>
          </button>
          <button onClick={() => setActiveImport('screenshot')}
            className="rounded-xl border-2 border-slate-200 bg-white p-4 text-left hover:border-teal-300 hover:shadow-sm transition-all">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-teal-600" />
              <h4 className="font-bold text-slate-900 text-sm">Screenshots (AI)</h4>
            </div>
            <p className="text-xs text-slate-500">Upload up to 20 screenshots — AI extracts tags with confidence scores.</p>
          </button>
        </div>
      )}

      {/* ═══ GOODREADS / STORYGRAPH CSV IMPORT ═══ */}
      {(activeImport === 'goodreads' || activeImport === 'storygraph') && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">
              {activeImport === 'goodreads' ? 'Goodreads' : 'StoryGraph'} CSV Import
            </h3>
            <Button variant="outline" size="sm" onClick={() => { setActiveImport(null); setCsvData(null); setCsvPreview(null); setCsvResult(null); }}>
              Back
            </Button>
          </div>

          {!csvData && (
            <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center">
              <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-3">
                Upload your {activeImport === 'goodreads' ? 'Goodreads' : 'StoryGraph'} export CSV
              </p>
              <p className="text-xs text-slate-400 mb-4">
                {activeImport === 'goodreads'
                  ? 'Go to Goodreads → My Books → Import/Export → Export Library'
                  : 'Go to StoryGraph → Settings → Export → Download CSV'}
              </p>
              <input type="file" accept=".csv" ref={csvFileRef} onChange={handleCsvUpload} className="hidden" />
              <Button onClick={() => csvFileRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" /> Choose CSV File
              </Button>
            </div>
          )}

          {csvData && csvFormat === 'unknown' && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700 font-semibold mb-1">Unrecognised CSV format</p>
              <p className="text-xs text-red-600">
                This doesn't look like a {activeImport === 'goodreads' ? 'Goodreads' : 'StoryGraph'} export.
                Columns found: {Object.keys(csvData[0] || {}).join(', ')}
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => { setCsvData(null); setCsvPreview(null); }}>
                Try Again
              </Button>
            </div>
          )}

          {csvPreview && csvFormat !== 'unknown' && !csvResult && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-bold text-slate-800 mb-3">Preview</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-teal-50 p-3 text-center">
                  <p className="text-2xl font-bold text-teal-700">{csvPreview.newBooks}</p>
                  <p className="text-[10px] text-teal-600 uppercase font-semibold">New books</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-700">{csvPreview.matched}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Already in DB</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-400">{csvPreview.skipped}</p>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Skipped</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {csvPreview.total} rows in CSV. New books will be looked up on Google Books and added to the database with auto-detected tags.
                Existing matches will have their tags updated.
              </p>
              <div className="flex gap-2">
                <Button onClick={handleCsvImport} disabled={csvImporting}>
                  {csvImporting ? 'Importing...' : `Import ${csvPreview.total - csvPreview.skipped} Books`}
                </Button>
                <Button variant="outline" onClick={() => { setCsvData(null); setCsvPreview(null); }}>Cancel</Button>
              </div>
              {csvImporting && <Progress value={50} className="mt-3" />}
            </div>
          )}

          {csvResult && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-bold text-green-800 mb-1">Import Complete</p>
              <p className="text-xs text-green-700">{csvResult.added} books added to database</p>
              <p className="text-xs text-green-700">{csvResult.matchedExisting} matched existing records</p>
              {csvResult.errors > 0 && <p className="text-xs text-red-600">{csvResult.errors} skipped (no match found)</p>}
              <Button variant="outline" size="sm" className="mt-3" onClick={onClose}>Done</Button>
            </div>
          )}
        </div>
      )}

      {/* ═══ BATCH SCREENSHOT IMPORT ═══ */}
      {activeImport === 'screenshot' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Batch Screenshot Import</h3>
            <Button variant="outline" size="sm" onClick={() => { setActiveImport(null); setScreenshots([]); setSsResults([]); setSsImportResult(null); }}>
              Back
            </Button>
          </div>

          {/* Upload area */}
          {screenshots.length === 0 && ssResults.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center">
              <Sparkles className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-3">Upload up to 20 screenshots from Romance.io, StoryGraph, etc.</p>
              <p className="text-xs text-slate-400 mb-4">AI will extract book titles, authors, and tags from each image.</p>
              <input type="file" accept="image/*" multiple ref={ssFileRef} onChange={handleScreenshotUpload} className="hidden" />
              <Button onClick={() => ssFileRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" /> Choose Screenshots
              </Button>
            </div>
          )}

          {/* Files selected, ready to process */}
          {screenshots.length > 0 && ssResults.length === 0 && !ssProcessing && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-800 mb-2">{screenshots.length} screenshot{screenshots.length !== 1 ? 's' : ''} selected</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {screenshots.map((f, i) => (
                  <div key={i} className="h-16 w-12 rounded-lg bg-slate-100 overflow-hidden">
                    <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>
              <Button onClick={processScreenshots}>
                <Sparkles className="h-4 w-4 mr-1" /> Process with AI
              </Button>
            </div>
          )}

          {/* Processing */}
          {ssProcessing && (
            <div className="rounded-xl border border-teal-200 bg-teal-50 p-6 text-center">
              <div className="animate-spin h-8 w-8 border-2 border-teal-600 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-sm font-semibold text-teal-800 mb-2">Processing image {ssProgress.current} of {ssProgress.total}</p>
              <Progress value={(ssProgress.current / ssProgress.total) * 100} className="max-w-xs mx-auto" />
            </div>
          )}

          {/* Review results */}
          {ssResults.length > 0 && !ssImportResult && (
            <div>
              {/* Tab navigation */}
              <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                {ssResults.map((res, i) => (
                  <button key={i} onClick={() => setSsActiveTab(i)}
                    className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                      ssActiveTab === i
                        ? 'bg-teal-600 text-white'
                        : res.error ? 'bg-red-100 text-red-600' : res.needsTitle ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}>
                    {res.title ? res.title.substring(0, 20) + (res.title.length > 20 ? '...' : '') : `Image ${i + 1}`}
                  </button>
                ))}
              </div>

              {/* Active tab content */}
              {ssResults[ssActiveTab] && (() => {
                const book = ssResults[ssActiveTab];
                const idx = ssActiveTab;
                return (
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex gap-4 mb-4">
                      <img src={book.imageSrc} alt="" className="h-32 rounded-lg border border-slate-200 object-contain flex-shrink-0" />
                      <div className="flex-1">
                        {book.error ? (
                          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                            <p className="text-xs text-red-700 font-semibold">Error processing this image</p>
                            <p className="text-[10px] text-red-600">{book.error}</p>
                          </div>
                        ) : (
                          <>
                            <div className="mb-2">
                              <label className="text-[10px] font-semibold text-slate-500 uppercase">Title</label>
                              <input type="text" value={book.title || ''}
                                onChange={e => updateSsResult(idx, 'title', e.target.value)}
                                placeholder="Enter book title..."
                                className={`w-full rounded-lg border px-3 py-1.5 text-sm ${book.needsTitle && !book.title ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`} />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-slate-500 uppercase">Author</label>
                              <input type="text" value={book.author || ''}
                                onChange={e => updateSsResult(idx, 'author', e.target.value)}
                                placeholder="Enter author..."
                                className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm" />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Tags by section */}
                    {!book.error && (
                      <div className="space-y-2">
                        {BOOK_TAXONOMY.filter(sec => book.tags[sec.key]).map(section => (
                          <div key={section.key}>
                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{section.title}</p>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(book.tags[section.key]).map(([item, val]) => {
                                const conf = book.confidence?.[section.key]?.[item] || 'medium';
                                return val === 'present' && (
                                  <span key={item} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${CONF_COLORS[conf]}`}>
                                    {item}
                                    <button onClick={() => removeSsTag(idx, section.key, item)}
                                      className="ml-1 opacity-60 hover:opacity-100">×</button>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {Object.keys(book.tags).length === 0 && (
                          <p className="text-xs text-slate-400 text-center py-2">No tags detected from this screenshot.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Import all button */}
              <div className="mt-4 flex gap-2">
                <Button onClick={handleSsImport} disabled={ssImporting}>
                  {ssImporting ? 'Importing...' : `Import All (${ssResults.filter(r => r.title && !r.error).length} books)`}
                </Button>
                <Button variant="outline" onClick={() => { setScreenshots([]); setSsResults([]); }}>Start Over</Button>
              </div>
            </div>
          )}

          {/* Import result */}
          {ssImportResult && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-bold text-green-800 mb-1">Screenshot Import Complete</p>
              <p className="text-xs text-green-700">{ssImportResult.imported} book{ssImportResult.imported !== 1 ? 's' : ''} imported</p>
              {ssImportResult.needsTitle > 0 && <p className="text-xs text-amber-600">{ssImportResult.needsTitle} skipped (no title)</p>}
              {ssImportResult.errors > 0 && <p className="text-xs text-red-600">{ssImportResult.errors} error{ssImportResult.errors !== 1 ? 's' : ''}</p>}
              <Button variant="outline" size="sm" className="mt-3" onClick={onClose}>Done</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
